import { Typography } from "@mui/material";
import BigNumberDisplay from "components/BigNumberDisplay";
import { Popup } from "components/Popup";
import useNotification from "hooks/useNotification";
import { jettonDeployController } from "lib/deploy-controller";
import { useState } from "react";
import useJettonStore from "store/jetton-store/useJettonStore";
import { Address } from "ton";
import { AppButton } from "components/appButton";
import { AppNumberInput } from "components/appInput";
import { useTonAddress, useTonConnectUI } from "@tonconnect/ui-react";
import { useNetwork } from "lib/hooks/useNetwork";
import { useRecoilState } from "recoil";
import { jettonActionsState } from "./jettonActions";
import BN from "bn.js";
import { parsePositiveTokenAmount } from "lib/amount";

function MintJettonsAction() {
  const [amount, setAmount] = useState<string>("");
  const [open, setOpen] = useState(false);
  const [actionInProgress, setActionInProgress] = useRecoilState(jettonActionsState);
  const [tonConnectUI] = useTonConnectUI();
  const { jettonMaster, isAdmin, symbol, getJettonDetails, isMyWallet, decimals } =
    useJettonStore();
  const walletAddress = useTonAddress();
  const { showNotification } = useNotification();
  const { network } = useNetwork();

  if (!isAdmin || !isMyWallet || !decimals) {
    return null;
  }

  const onMint = async () => {
    if (!jettonMaster) {
      return;
    }

    if (!amount) {
      showNotification(`Minimum amount of ${symbol} to mint is 1`, "warning");
      return;
    }

    let value: BN;
    try {
      value = parsePositiveTokenAmount(amount, decimals, "Mint");
    } catch (error) {
      showNotification(error instanceof Error ? error.message : "Invalid mint amount", "warning");
      return;
    }
    try {
      setActionInProgress(true);
      const outcome = await jettonDeployController.mint(
        tonConnectUI,
        Address.parse(jettonMaster),
        value,
        walletAddress,
        network,
      );
      setOpen(false);
      if (!(await getJettonDetails())) return;
      if (outcome.status === "confirmed") {
        const message = (
          <>
            Successfully minted <BigNumberDisplay value={amount} /> {symbol}
          </>
        );
        showNotification(message, "success");
      } else {
        showNotification(
          "Mint transaction was submitted, but final confirmation is still pending. Check the explorer before retrying.",
          "warning",
        );
      }
    } catch (error) {
      console.log(error);
      if (error instanceof Error) {
        showNotification(error.message, "error");
      }
    } finally {
      setActionInProgress(false);
      setOpen(false);
    }
  };

  const onClose = () => {
    setAmount("");
    setOpen(false);
  };

  return (
    <>
      <Popup open={open && !actionInProgress} onClose={onClose} maxWidth={400}>
        <>
          <Typography className="title">Mint {symbol}</Typography>
          <AppNumberInput label={`Enter ${symbol} amount`} value={amount} onChange={setAmount} />
          <AppButton onClick={onMint}>Submit</AppButton>
        </>
      </Popup>
      <AppButton loading={actionInProgress} transparent={true} onClick={() => setOpen(true)}>
        Mint
      </AppButton>
    </>
  );
}

export default MintJettonsAction;
